import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { 
  Shield, 
  Upload, 
  Mail, 
  FileText, 
  Download,
  Loader2,
  AlertTriangle,
  Clock,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AttestationBlockModal = ({ isOpen, status, onSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [helpDocs, setHelpDocs] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchHelpDocs();
      // Prevent scrolling on body when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Prevent back button navigation
  useEffect(() => {
    if (isOpen) {
      const handlePopState = (e) => {
        e.preventDefault();
        window.history.pushState(null, '', window.location.href);
      };
      
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
      
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isOpen]);

  const fetchHelpDocs = async () => {
    try {
      const response = await fetch(`${API}/documents/av-urssaf`, { credentials: 'include' });
      if (response.ok) {
        const docs = await response.json();
        setHelpDocs(docs);
      }
    } catch (error) {
      console.error('Failed to fetch help docs:', error);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptes');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/attestations/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        toast.success('Attestation deposee avec succes. En attente de validation.');
        onSuccess?.();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors du depot');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors du depot');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadHelpDoc = async (docId, filename) => {
    try {
      const response = await fetch(`${API}/documents/${docId}/download`, {
        credentials: 'include',
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  if (!isOpen) return null;

  const isPending = status?.status === 'pending';
  const isExpired = status?.status === 'expired';

  return (
    <>
      {/* Full screen overlay - cannot be dismissed */}
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm" />
      
      {/* Modal - centered, no close button */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          {/* Header - no close button */}
          <div className={`p-6 border-b ${isPending ? 'bg-amber-50' : 'bg-red-50'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${isPending ? 'bg-amber-100' : 'bg-red-100'}`}>
                {isPending ? (
                  <Clock className="w-6 h-6 text-amber-600" />
                ) : (
                  <Lock className="w-6 h-6 text-red-600" />
                )}
              </div>
              <div>
                <h2 className={`text-xl font-bold ${isPending ? 'text-amber-800' : 'text-red-800'}`}>
                  {isPending 
                    ? "Attestation en cours de traitement" 
                    : isExpired 
                      ? "Attestation expiree"
                      : "Attestation de vigilance requise"
                  }
                </h2>
                <p className={`text-sm mt-1 ${isPending ? 'text-amber-600' : 'text-red-600'}`}>
                  {isPending 
                    ? "Votre compte sera debloque apres validation"
                    : "Acces au compte bloque"
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Main message */}
            <div className={`p-4 rounded-lg border-2 ${
              isPending 
                ? 'bg-amber-50 border-amber-300' 
                : 'bg-red-50 border-red-300'
            }`}>
              <div className="flex items-start gap-3">
                <Shield className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                  isPending ? 'text-amber-600' : 'text-red-600'
                }`} />
                <div>
                  <p className={`font-semibold ${isPending ? 'text-amber-800' : 'text-red-800'}`}>
                    {isPending ? 'En attente de validation' : 'Action obligatoire'}
                  </p>
                  <p className={`text-sm mt-1 ${isPending ? 'text-amber-700' : 'text-red-700'}`}>
                    {status?.message}
                  </p>
                  {isExpired && (
                    <p className="text-sm mt-2 text-red-700 font-medium">
                      Votre attestation doit etre renouvelee.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Upload section - only if not pending */}
            {!isPending && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-indigo-300 rounded-xl p-6 text-center bg-indigo-50">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".pdf"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <Upload className="w-12 h-12 mx-auto text-indigo-400 mb-3" />
                  <p className="text-slate-700 mb-4 font-medium">
                    Deposez votre attestation de vigilance URSSAF
                  </p>
                  <p className="text-sm text-slate-500 mb-4">
                    Format accepte : PDF uniquement
                  </p>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    size="lg"
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 mr-2" />
                        Choisir un fichier PDF
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Pending state message */}
            {isPending && (
              <div className="bg-amber-50 rounded-xl p-6 text-center border border-amber-200">
                <Loader2 className="w-10 h-10 mx-auto text-amber-500 animate-spin mb-3" />
                <p className="text-amber-800 font-medium">
                  Votre attestation est en cours de verification
                </p>
                <p className="text-sm text-amber-600 mt-2">
                  Vous serez notifie par email une fois validee. Cela peut prendre quelques heures.
                </p>
              </div>
            )}

            {/* Help documents */}
            {helpDocs.length > 0 && !isPending && (
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2 text-slate-800">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  Comment obtenir mon attestation de vigilance URSSAF ?
                </h3>
                <div className="space-y-2">
                  {helpDocs.map((doc) => (
                    <div 
                      key={doc.document_id} 
                      className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-blue-800">{doc.name}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadHelpDoc(doc.document_id, `${doc.name}.${doc.file_type}`)}
                        className="text-blue-600 border-blue-300 hover:bg-blue-200"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Telecharger
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Support contact */}
            <div className="bg-slate-100 rounded-xl p-4 border border-slate-200">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-white rounded-lg">
                  <Mail className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Besoin d'aide ?</p>
                  <p className="text-sm text-slate-600 mt-1">
                    Contactez notre equipe support :
                  </p>
                  <a 
                    href="mailto:info@nerium-lnc.com" 
                    className="text-indigo-600 hover:text-indigo-700 hover:underline font-semibold text-lg"
                  >
                    info@nerium-lnc.com
                  </a>
                </div>
              </div>
            </div>

            {/* Warning notice */}
            <div className="flex items-start gap-2 text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>
                L'attestation de vigilance est obligatoire pour maintenir l'acces a votre compte.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
